/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Out-of-plane (improper) force constants for sp² centers, keyed by the central
 * atom's MMFF-lite type. V = ½·k·h² with h the out-of-plane distance (Å), so k
 * is in eV/Å². Values are scaled so the out-of-plane wag lands in the right
 * range (formaldehyde C wag ≈ 1170 cm⁻¹ ⇒ k ≈ 35 eV/Å²) and the center stays
 * within a few hundredths of an Å of planar at 300 K.
 *
 * Only 3-coordinate sp² centers get an improper (see buildMmffImpropers in
 * mmffParamLookup). Centers not listed here (sp³, sp, 2- or 4-coordinate) get
 * none — there is no out-of-plane term for them.
 */

export const MMFF_LITE_IMPROPER_K: Readonly<Record<string, number>> = {
  'C.2': 30, // sp² carbon — carbonyl/alkene; often terminal (no torsion) so it
  // genuinely needs a stiff out-of-plane term to stay planar (formaldehyde).
  'C.ar': 2, // aromatic carbon — ring planarity is already held by the n=2
  // torsions, so this is only a gentle assist. A STIFF value here is redundant
  // AND pushes the out-of-plane C–H bend (benzene's strong ~673 band) up past
  // 1000 cm⁻¹ while killing its intensity — keep it low so the band lands right.
  'N.2': 20, // sp² nitrogen — imine
};

/** Out-of-plane force constant (eV/Å²) for an sp² center type, or null. */
export const lookupImproperK = (centerType: string): number | null => {
  const v = MMFF_LITE_IMPROPER_K[centerType];
  return v === undefined ? null : v;
};

// Hydrogen-out-of-plane (Wilson) improper: keeps an sp² C–H hydrogen in the
// plane of its carbon's two other substituents. The center-out-of-plane
// improper above keeps the CARBON planar; this one keeps the H planar, which is
// what governs the strong out-of-plane C–H bend band (aromatic ~675 cm⁻¹, used
// to read ring substitution; alkene ~700–1000, used for cis/trans). Calibrated
// (headless IR through the real engine) so the benzene wag lands near 675.
export const HYDROGEN_IMPROPER_K = 1.0; // eV/Å²
